import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  consentTimeClock,
  createTimeClockAdjustment,
  createTimeClockMark,
  downloadTimeClockExport,
  fetchTimeClockEmployee,
  fetchTimeClockHoursBank,
  fetchTimeClockMarks,
  fetchTimeClockMirror,
  fetchTimeClockPolicy,
  updateTimeClockEmployee,
} from "../../../api/client";
import { isInternshipPolicy, timeClockCopy } from "../../VirtualOffice/attendanceCopy";
import { bankLabel } from "../../VirtualOffice/jornadaAlertsCopy";
import {
  can,
  scopeSetFromPermissions,
  useEffectivePermissions,
} from "../../../hooks/useEffectivePermissions";
import { useAuthContext } from "../../../context/AuthContext";
import ApiErrorAlertBody, {
  resolveApiErrorNotice,
} from "../../../components/ApiErrorAlertBody";

function fmtMin(m) {
  if (m == null) return "—";
  const n = Number(m) || 0;
  const h = Math.floor(n / 60);
  const min = n % 60;
  return `${h}h${String(min).padStart(2, "0")}m`;
}

function markTypeLabel(type) {
  switch (type) {
    case "entry":
      return "Entrada";
    case "exit":
      return "Saída";
    case "break_start":
      return "Início intervalo";
    case "break_end":
      return "Fim intervalo";
    default:
      return type;
  }
}

function markSourceLabel(source) {
  switch (String(source || "")) {
    case "virtual_office_join":
      return "VO ingresso";
    case "virtual_office_leave":
      return "VO saída";
    case "manual":
      return "Manual";
    case "adjustment":
      return "Ajuste";
    default:
      return source || "—";
  }
}

function markLeaveReasonHint(mark) {
  let info = mark?.device_info_json;
  if (typeof info === "string") {
    try {
      info = JSON.parse(info);
    } catch {
      info = null;
    }
  }
  const reason = String(info?.leaveReason || "").trim();
  if (reason === "window_closed") {
    return info?.leaveReasonLabel || "Usuário fechou a janela";
  }
  return null;
}

/**
 * Espelho + marcações + export + justificativa + matrícula.
 * Usado no hub admin, analytics do usuário e Meu Ponto.
 */
export default function UserTimeClockPanel({
  userId,
  userName,
  selfMode = false,
  showMatricula = true,
  showManualMark = true,
  showAdjustment = true,
}) {
  const { userData } = useAuthContext();
  const qc = useQueryClient();
  const isMasterUser = Number(userData?.master) === 1;
  const { data: permData } = useEffectivePermissions();
  const scopeSet = useMemo(
    () =>
      scopeSetFromPermissions(permData, {
        ...userData,
        master: isMasterUser ? 1 : 0,
      }),
    [permData, userData, isMasterUser]
  );
  const canView = can(scopeSet, "time_clock", "view");
  const canExport = can(scopeSet, "time_clock", "export");
  const canAdjust = can(scopeSet, "time_clock", "adjust");
  const isSelf = Number(userId) === Number(userData?.id);

  const [start, setStart] = useState(() => dayjs().startOf("month"));
  const [end, setEnd] = useState(() => dayjs());
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [matricula, setMatricula] = useState("");
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjForm, setAdjForm] = useState({
    requestedMarkType: "entry",
    requestedMarkedAt: dayjs().format("YYYY-MM-DDTHH:mm"),
    justificativa: "",
    originalMarkId: "",
  });
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    markType: "entry",
    markedAt: dayjs().format("YYYY-MM-DDTHH:mm"),
  });
  const [consentOpen, setConsentOpen] = useState(false);

  const startStr = start?.format?.("YYYY-MM-DD");
  const endStr = end?.format?.("YYYY-MM-DD");

  useEffect(() => {
    setMatricula("");
    setMsg("");
  }, [userId]);

  const mirrorQuery = useQuery(
    ["time-clock-mirror", userId, startStr, endStr],
    async () => {
      const res = await fetchTimeClockMirror({
        userId,
        start: startStr,
        end: endStr,
      });
      return res?.data?.data || res?.data || {};
    },
    { enabled: Boolean(canView && userId && startStr && endStr) }
  );

  const marksQuery = useQuery(
    ["time-clock-marks", userId, startStr, endStr],
    async () => {
      const res = await fetchTimeClockMarks({
        userId,
        start: `${startStr} 00:00:00`,
        end: `${endStr} 23:59:59`,
      });
      return res?.data?.data || res?.data || [];
    },
    { enabled: Boolean(canView && userId && startStr && endStr) }
  );

  const employeeQuery = useQuery(
    ["time-clock-employee", userId],
    async () => {
      const res = await fetchTimeClockEmployee(userId);
      return res?.data?.data || res?.data || {};
    },
    { enabled: Boolean(canView && userId) }
  );

  const policyQuery = useQuery(
    ["time-clock-policy", userId],
    async () => {
      const res = await fetchTimeClockPolicy(userId);
      return res?.data?.data || res?.data || null;
    },
    { enabled: Boolean(canView && userId) }
  );

  const hoursBankQuery = useQuery(
    ["time-clock-hours-bank", userId, startStr, endStr],
    async () => {
      const res = await fetchTimeClockHoursBank({ userId, start: startStr, end: endStr });
      return res?.data?.data || res?.data || { balanceMinutes: 0, entries: [] };
    },
    { enabled: Boolean(canView && userId) }
  );

  useEffect(() => {
    if (!employeeQuery.data || employeeQuery.isFetching) return;
    if (Number(employeeQuery.data.user_id) !== Number(userId)) return;
    setMatricula(employeeQuery.data.matricula || "");
  }, [employeeQuery.data, employeeQuery.isFetching, userId]);

  const saveMatricula = useMutation(
    () => updateTimeClockEmployee(userId, { matricula }),
    {
      onSuccess: () => {
        setMsg("Matrícula salva.");
        qc.invalidateQueries(["time-clock-employee", userId]);
      },
      onError: (e) => setMsg(e?.response?.data?.message || e?.message || "Erro ao salvar matrícula"),
    }
  );

  const createAdj = useMutation(
    () =>
      createTimeClockAdjustment({
        userId,
        requestedMarkType: adjForm.requestedMarkType,
        requestedMarkedAt: String(adjForm.requestedMarkedAt).replace("T", " ") + ":00",
        justificativa: adjForm.justificativa,
        originalMarkId: adjForm.originalMarkId ? Number(adjForm.originalMarkId) : null,
      }),
    {
      onSuccess: () => {
        setMsg("Justificativa enviada para análise.");
        setAdjOpen(false);
        qc.invalidateQueries(["time-clock-adjustments"]);
      },
      onError: (e) => setMsg(e?.response?.data?.message || e?.message || "Erro ao enviar justificativa"),
    }
  );

  const createManual = useMutation(
    () =>
      createTimeClockMark({
        userId,
        markType: manualForm.markType,
        markedAt: String(manualForm.markedAt).replace("T", " ") + ":00",
      }),
    {
      onSuccess: () => {
        setMsg("Marcação manual registrada.");
        setManualOpen(false);
        qc.invalidateQueries(["time-clock-mirror", userId]);
        qc.invalidateQueries(["time-clock-marks", userId]);
      },
      onError: (e) => setMsg(e?.response?.data?.message || e?.message || "Erro ao marcar"),
    }
  );

  if (!canView) {
    return (
      <Alert severity="info">
        Sem permissão para visualizar o espelho de ponto deste colaborador.
      </Alert>
    );
  }

  const mirror = mirrorQuery.data || {};
  const days = mirror.days || [];
  const totals = mirror.totals || {};
  const marks = marksQuery.data || [];
  const canEditMatricula = canAdjust;
  const canRequestAdj = showAdjustment && (isSelf || canAdjust);
  const canManual = showManualMark && (isSelf || canAdjust);

  const runExport = async (kind) => {
    setBusy(kind);
    setMsg("");
    try {
      await downloadTimeClockExport(kind, {
        userId,
        start: startStr,
        end: endStr,
      });
      setMsg(`Arquivo ${String(kind).toUpperCase()} baixado.`);
    } catch (e) {
      setMsg(e?.response?.data?.message || e?.message || "Falha na exportação");
    } finally {
      setBusy("");
    }
  };

  const policy =
    policyQuery.data ||
    employeeQuery.data?.attendancePolicy ||
    mirrorQuery.data?.attendancePolicy ||
    null;
  const clockCopy = timeClockCopy(policy);
  const isExempt = Boolean(clockCopy.exempt);
  const isInternship = isInternshipPolicy(policy);
  const consentText = policy?.consent || policyQuery.data?.consent || null;
  const consented = Boolean(
    policyQuery.data?.consented ?? employeeQuery.data?.consented
  );
  const needsConsent = Boolean(!isExempt && consentText && isSelf && !consented);

  const requireConsentFirst = () => {
    if (!needsConsent) return false;
    setConsentOpen(true);
    setMsg("Registre a ciência do controle de horário antes de marcar.");
    return true;
  };

  const handleConsent = async () => {
    if (!consentText?.body) {
      setMsg("Não há texto de ciência para este regime.");
      return;
    }
    if (!consentOpen) {
      setConsentOpen(true);
      return;
    }
    try {
      await consentTimeClock({ textVersion: consentText?.version });
      setMsg("Ciência do controle de horário registrada.");
      setConsentOpen(false);
      qc.invalidateQueries(["time-clock-employee", userId]);
      qc.invalidateQueries(["time-clock-policy", userId]);
    } catch (e) {
      setMsg(e?.response?.data?.message || e?.message || "Erro no consentimento");
    }
  };

  return (
    <Box sx={{ mt: selfMode ? 0 : 2 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
        {selfMode
          ? clockCopy.titleSelf
          : `${clockCopy.titleOther} — ${userName || `Usuário #${userId}`}`}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {clockCopy.intro}
        {!isExempt && consented
          ? " Ciência já registrada para a versão vigente do texto."
          : !isExempt
            ? " Ciência do controle de horário ainda não registrada."
            : null}
      </Typography>
      {isExempt ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {clockCopy.intro} Este perfil não aparece em AFD, hora extra, atraso ou falta.
        </Alert>
      ) : null}
      {mirror.legacyMarksDisclaimer ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Há marcações geradas antes da política por tipo de contrato. Elas permanecem
          imutáveis e não valem como jornada deste regime.
        </Alert>
      ) : null}

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
          <DatePicker
            label="Início"
            value={start}
            onChange={(v) => setStart(v)}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="Fim"
            value={end}
            onChange={(v) => setEnd(v)}
            slotProps={{ textField: { size: "small" } }}
          />
        </Stack>
      </LocalizationProvider>

      {msg ? (
        <Alert
          severity={
            msg.toLowerCase().includes("falha") || msg.toLowerCase().includes("erro")
              ? "error"
              : "success"
          }
          sx={{ mb: 2 }}
          onClose={() => setMsg("")}
        >
          {msg}
        </Alert>
      ) : null}

      {showMatricula && !isExempt ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }} alignItems="flex-start">
          <TextField
            size="small"
            label="Matrícula (AFD)"
            value={matricula}
            onChange={(e) => setMatricula(e.target.value)}
            disabled={!canEditMatricula || employeeQuery.isFetching}
            sx={{ minWidth: 220 }}
          />
          {canEditMatricula ? (
            <Button
              size="small"
              variant="outlined"
              disabled={
                saveMatricula.isLoading ||
                employeeQuery.isFetching ||
                Number(employeeQuery.data?.user_id) !== Number(userId)
              }
              onClick={() => saveMatricula.mutate()}
            >
              Salvar matrícula
            </Button>
          ) : null}
        </Stack>
      ) : null}

      <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        {canExport && !isExempt ? (
          <>
            <Button size="small" variant="outlined" disabled={Boolean(busy)} onClick={() => runExport("payroll")}>
              {busy === "payroll" ? "…" : "Espelho TXT"}
            </Button>
            <Button size="small" variant="outlined" disabled={Boolean(busy)} onClick={() => runExport("payroll-pdf")}>
              {busy === "payroll-pdf" ? "…" : "Espelho PDF"}
            </Button>
            <Button size="small" variant="outlined" disabled={Boolean(busy)} onClick={() => runExport("afd")}>
              {busy === "afd" ? "…" : "AFD"}
            </Button>
            <Button size="small" variant="outlined" disabled={Boolean(busy)} onClick={() => runExport("aej")}>
              {busy === "aej" ? "…" : "AEJ"}
            </Button>
          </>
        ) : null}
        {canRequestAdj && !isExempt ? (
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              if (requireConsentFirst()) return;
              setAdjOpen(true);
            }}
          >
            Solicitar justificativa
          </Button>
        ) : null}
        {canManual && !isExempt ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              if (requireConsentFirst()) return;
              setManualOpen(true);
            }}
          >
            Marcação manual
          </Button>
        ) : null}
        {isSelf && clockCopy.consentButton ? (
          <Button size="small" onClick={handleConsent}>
            {clockCopy.consentButton}
          </Button>
        ) : null}
      </Stack>

      {mirrorQuery.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          <ApiErrorAlertBody
            {...resolveApiErrorNotice(
              mirrorQuery.error,
              "Não foi possível carregar o espelho de ponto. Atualize a página ou tente outro período."
            )}
          />
        </Alert>
      ) : (
        <Typography variant="body2" sx={{ mb: 1 }}>
          Totais do período: trabalhadas {fmtMin(totals.workedMinutes)}
          {isInternship
            ? ` · excedente do teto ${fmtMin(totals.excessMinutes)}`
            : isExempt
              ? ""
              : ` · extras ${fmtMin(totals.overtimeMinutes)} · déficit ${fmtMin(totals.deficitMinutes)}`}
          {isInternship && mirror.internship?.weeklyExceeded
            ? " · teto semanal da Lei 11.788 ultrapassado"
            : ""}
          {!isExempt && !isInternship && hoursBankQuery.data
            ? ` · banco de horas ${bankLabel(hoursBankQuery.data.balanceMinutes)}`
            : ""}
        </Typography>
      )}

      {mirrorQuery.isLoading ? (
        <Typography variant="body2">Carregando espelho…</Typography>
      ) : mirrorQuery.isError ? null : (
        <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Data</TableCell>
                <TableCell>Marcações</TableCell>
                <TableCell align="right">Trabalhado</TableCell>
                {isExempt ? null : <TableCell align="right">Esperado</TableCell>}
                {isExempt ? null : (
                  <TableCell align="right">{clockCopy.extraLabel}</TableCell>
                )}
                {isExempt || isInternship ? null : (
                  <TableCell align="right">{clockCopy.deficitLabel}</TableCell>
                )}
                {isExempt || isInternship ? null : <TableCell>{clockCopy.lateLabel}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {days.map((day) => (
                <TableRow key={day.date} hover>
                  <TableCell>{day.date}</TableCell>
                  <TableCell>
                    {(day.marks || [])
                      .map((m) => {
                        const base = `${markTypeLabel(m.mark_type)} ${String(m.marked_at).slice(11, 16)}`;
                        const hint = markLeaveReasonHint(m);
                        return hint ? `${base} (${hint})` : base;
                      })
                      .join(" · ") || "—"}
                  </TableCell>
                  <TableCell align="right">{fmtMin(day.workedMinutes)}</TableCell>
                  {isExempt ? null : (
                    <TableCell align="right">{fmtMin(day.expectedMinutes)}</TableCell>
                  )}
                  {isExempt ? null : (
                    <TableCell align="right">
                      {fmtMin(isInternship ? day.excessDailyMinutes : day.overtimeMinutes)}
                    </TableCell>
                  )}
                  {isExempt || isInternship ? null : (
                    <TableCell align="right">{fmtMin(day.deficitMinutes)}</TableCell>
                  )}
                  {isExempt || isInternship ? null : (
                    <TableCell>{day.late ? "Sim" : "Não"}</TableCell>
                  )}
                </TableRow>
              ))}
              {!days.length ? (
                <TableRow>
                  <TableCell colSpan={isExempt ? 3 : isInternship ? 5 : 7} align="center">
                    Sem marcações no período.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {isExempt ? null : (
        <>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Timeline de marcações
      </Typography>
      {marksQuery.isError ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {marksQuery.error?.message ||
            "Não foi possível carregar as marcações. Tente de novo em instantes."}
        </Alert>
      ) : null}
      <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
        <Table size="small">
            <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>NSR</TableCell>
              <TableCell>Data/hora</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Origem</TableCell>
              <TableCell>Obs.</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {marks.map((m) => (
              <TableRow key={m.id} hover>
                <TableCell>{m.id}</TableCell>
                <TableCell>{m.nsr}</TableCell>
                <TableCell>{String(m.marked_at).slice(0, 19)}</TableCell>
                <TableCell>{markTypeLabel(m.mark_type)}</TableCell>
                <TableCell>{markSourceLabel(m.source)}</TableCell>
                <TableCell>{markLeaveReasonHint(m) || "—"}</TableCell>
              </TableRow>
            ))}
            {!marks.length && !marksQuery.isError ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Sem registros no período.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
        </>
      )}

      <Dialog open={adjOpen} onClose={() => setAdjOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Solicitar justificativa</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo solicitado</InputLabel>
              <Select
                label="Tipo solicitado"
                value={adjForm.requestedMarkType}
                onChange={(e) => setAdjForm((p) => ({ ...p, requestedMarkType: e.target.value }))}
              >
                <MenuItem value="entry">Entrada</MenuItem>
                <MenuItem value="exit">Saída</MenuItem>
                <MenuItem value="break_start">Início intervalo</MenuItem>
                <MenuItem value="break_end">Fim intervalo</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Data e hora"
              type="datetime-local"
              size="small"
              value={adjForm.requestedMarkedAt}
              onChange={(e) => setAdjForm((p) => ({ ...p, requestedMarkedAt: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="ID da marcação original (opcional)"
              helperText="Use a coluna ID da timeline (não o NSR)"
              size="small"
              value={adjForm.originalMarkId}
              onChange={(e) => setAdjForm((p) => ({ ...p, originalMarkId: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Justificativa"
              multiline
              minRows={3}
              value={adjForm.justificativa}
              onChange={(e) => setAdjForm((p) => ({ ...p, justificativa: e.target.value }))}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdjOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={createAdj.isLoading || !String(adjForm.justificativa || "").trim()}
            onClick={() => createAdj.mutate()}
          >
            Enviar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manualOpen} onClose={() => setManualOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Marcação manual</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={manualForm.markType}
                onChange={(e) => setManualForm((p) => ({ ...p, markType: e.target.value }))}
              >
                <MenuItem value="entry">Entrada</MenuItem>
                <MenuItem value="exit">Saída</MenuItem>
                <MenuItem value="break_start">Início intervalo</MenuItem>
                <MenuItem value="break_end">Fim intervalo</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Data e hora"
              type="datetime-local"
              size="small"
              value={manualForm.markedAt}
              onChange={(e) => setManualForm((p) => ({ ...p, markedAt: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualOpen(false)}>Cancelar</Button>
          <Button variant="contained" disabled={createManual.isLoading} onClick={() => createManual.mutate()}>
            Registrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={consentOpen} onClose={() => setConsentOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{consentText?.title || clockCopy.consentButton}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap", mt: 1 }}>
            {consentText?.body || clockCopy.intro}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsentOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleConsent}>
            Li e concordo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
